import { fetchSectorMapping } from './stockMetadataBlob';

// Maps a stock ticker (e.g. "AADI") to its IDX sector name (e.g. "Energy"),
// matching the `ohlc/stocks/{sector}/{ticker}.parquet` blob layout. Cached in
// localStorage so OHLC path resolution never needs a backend round-trip after
// the first load, even across page reloads/sessions.
const STORAGE_KEY = 'bm_stock_to_sector_v1';
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours - sector assignments rarely change

type StockToSectorMap = { [stock: string]: string };

let memoryCache: StockToSectorMap | null = null;
let inFlight: Promise<StockToSectorMap> | null = null;

function readFromStorage(): StockToSectorMap | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.map || typeof parsed.map !== 'object' || !parsed.timestamp) return null;
    if (Date.now() - parsed.timestamp > TTL_MS) return null;
    return parsed.map as StockToSectorMap;
  } catch {
    return null;
  }
}

function writeToStorage(map: StockToSectorMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ map, timestamp: Date.now() }));
  } catch {
    // ignore storage errors (quota exceeded, private browsing, etc.)
  }
}

// Seed the cache with a map already fetched elsewhere (e.g. the page's own
// sector-mapping load), avoiding a redundant stockmetadata.parquet read.
export function primeStockToSectorMap(map: StockToSectorMap): void {
  if (!map || Object.keys(map).length === 0) return;
  memoryCache = map;
  writeToStorage(map);
}

export async function getStockToSectorMap(forceRefresh = false): Promise<StockToSectorMap> {
  if (memoryCache && !forceRefresh) return memoryCache;

  if (!forceRefresh) {
    const cached = readFromStorage();
    if (cached) {
      memoryCache = cached;
      return cached;
    }
  }

  if (inFlight) return inFlight;

  inFlight = fetchSectorMapping(forceRefresh).then(res => {
    const map = res.success && res.data?.stockToSector ? res.data.stockToSector : {};
    memoryCache = map;
    writeToStorage(map);
    return map;
  }).finally(() => {
    inFlight = null;
  });

  return inFlight;
}

// Builds the OHLC blob path for a ticker, e.g. "ohlc/stocks/Energy/AADI.parquet".
// Returns null if the ticker's sector is unknown.
// Automatically attempts a cache refresh if ticker is not found.
export async function getOhlcBlobPath(ticker: string): Promise<string | null> {
  let stockToSector = await getStockToSectorMap();
  let sector = stockToSector[ticker.toUpperCase()];
  if (!sector) {
    console.warn(`Sector for ticker ${ticker} not found in cache. Refreshing stock to sector map...`);
    stockToSector = await getStockToSectorMap(true);
    sector = stockToSector[ticker.toUpperCase()];
  }
  if (!sector) return null;
  return `ohlc/stocks/${sector}/${ticker.toUpperCase()}.parquet`;
}
